<template>
	<div class="my-2 flex w-full max-w-full flex-wrap gap-2">
		<BaseAlert
			:type="isPlanRecent(unusedValue) ? 'warning' : 'info'"
			v-for="(unusedValue, key) in unusedParticipants"
			:key="'unusedParticipant' + key"
			class="max-w-full text-sm"
		>
			<span class="font-bold">
				{{ unusedValue.fullName }}
			</span>
			{{ t('newSession.missingParticipantText') }}
			<span class="font-bold">
				{{ formatLastUpdated(unusedValue.lastUpdated) }}
			</span>
			<ButtonSmall
				@click.prevent="handleAddPlan(unusedValue)"
				class="ml-4 inline!"
				transparent
			>
				{{ t('general.add') }}
			</ButtonSmall>
			<span
				v-if="hydrationErrorIds.has(unusedValue.id)"
				class="ml-2 font-medium text-red-600"
			>
				{{ t('newSession.addParticipantError') }}
			</span>
		</BaseAlert>
	</div>
</template>

<script setup lang="ts">
import BaseAlert from '@/components/common/BaseAlert.vue'
import ButtonSmall from '@/components/forms/ButtonSmall.vue'
import { type PlanVersion, usePlanVersionsV2 } from '@/mixins/planVersionsV2'
import type { PlanData } from '@/types'
import { formatLastUpdated } from '@/views/NewSession/PlanVersions/utils'
import { computed, type PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
	plans: {
		type: Array as PropType<Array<Partial<PlanData>>>,
		required: true,
	},
	unusedParticipants: {
		type: Object as PropType<Record<string, PlanVersion>>,
	},
})

const { getPlanBasicDataForVersion } = usePlanVersionsV2()

const emit = defineEmits(['addPlan'])

const hydrationErrorIds = ref<Set<string>>(new Set())

const isPlanRecent = (version: PlanVersion) => {
	if (!oldestPlanTimeStamp.value) {
		return true
	}
	return (
		Date.parse(version.lastUpdated!) >= Date.parse(oldestPlanTimeStamp.value)
	)
}

// figure out the oldest plan in this appointment
const oldestPlanTimeStamp = computed(() => {
	return props.plans.reduce<string | undefined>((oldest, plan) => {
		if (!oldest || Date.parse(oldest) > Date.parse(plan.lastUpdated!)) {
			return plan.lastUpdated
		}
		return oldest
	}, undefined)
})

const handleAddPlan = async (version: PlanVersion) => {
	// hydrate the plan basic data from the source it was found in, then send it up
	const planData = await getPlanBasicDataForVersion(version)

	if (!planData) {
		hydrationErrorIds.value = new Set(hydrationErrorIds.value).add(version.id)
		return
	}

	hydrationErrorIds.value.delete(version.id)
	emit('addPlan', { plan: planData, source: version.source })
}
</script>
